/**
 * The parts of GIF export that the UI needs synchronously while rendering: whether the option can
 * be offered at all, and the length limit.
 *
 * These live apart from `mp4ToGif` on purpose. That module pulls in the demuxer and encoder, which
 * are only worth loading when someone actually converts; importing it just to ask "is this
 * supported?" would drag both into the main bundle and undo the lazy loading.
 */

/**
 * Videos longer than this are refused outright.
 *
 * Memory sets the limit, not time. The encoder accumulates the whole GIF before it can be handed
 * over, so peak heap grows with frame count: measured in Chrome against 1080p30 sources, a 60s
 * video peaks around 330MB (~11s, ~7MB output) while 120s reaches ~500MB (~21s, ~14MB output).
 * 60s keeps that comfortable on a low-memory machine and keeps the output small enough to embed.
 */
export const GIF_MAX_DURATION_SECONDS = 60;

/**
 * Why the GIF option is unavailable, or null when it is available. Both surfaces show this as the
 * tile's tooltip, since neither reason is something a user could infer from a greyed-out button.
 *
 * A duration of 0 means "not known yet" -- Luminary reports it only once the video has loaded, and
 * the download tiles are on screen before that -- so it is deliberately not treated as too short.
 * Claiming a 15s video is over the limit while it loads would be actively wrong; `convertMp4ToGif`
 * re-checks the real demuxed duration anyway, so nothing slips through.
 */
export function getGifUnavailableReason(durationSeconds: number): string | null {
    if (!isGifConversionSupported()) {
        return "GIF export isn't supported in this browser. Try Chrome, Edge or Safari.";
    }
    if (durationSeconds > 0 && !isDurationConvertibleToGif(durationSeconds)) {
        const minutes = GIF_MAX_DURATION_SECONDS / 60;
        const limit =
            GIF_MAX_DURATION_SECONDS % 60 === 0
                ? `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`
                : `${GIF_MAX_DURATION_SECONDS} seconds`;
        return `GIF export is available for videos under ${limit}.`;
    }
    return null;
}

/**
 * Whether this browser can convert at all. WebCodecs is the only decode path implemented -- there
 * is deliberately no seek-and-capture fallback -- so callers must disable the option when this is
 * false and say the browser is unsupported.
 */
export function isGifConversionSupported(): boolean {
    return typeof window !== 'undefined' && typeof window.VideoDecoder !== 'undefined';
}

/** Whether a video of this length is short enough to convert. Duration is in seconds. */
export function isDurationConvertibleToGif(durationSeconds: number): boolean {
    return durationSeconds > 0 && durationSeconds <= GIF_MAX_DURATION_SECONDS;
}
